export class Money {
    private readonly cents: number;

    private constructor(cents: number) {
        if (!Number.isInteger(cents)) {
            throw new Error('Cents must be an integer');
        }
        if (cents < 0) {
            throw new Error('Cents cannot be negative');
        }
        this.cents = cents;
    }

    /**
     * Создает экземпляр Money из копеек
     * @param cents - сумма в копейках (целое число)
     */
    static fromCents(cents?: number): Money {
        return new Money(cents);
    }

    /**
     * Создает экземпляр Money из обычной денежной суммы
     * @param amount - денежная сумма (например: 3.89)
     */
    static fromAmount(amount: number): Money {
        if (amount < 0) {
            throw new Error('Amount cannot be negative');
        }
        const cents = Math.round(amount * 100);
        return new Money(cents);
    }

    /**
     * Создание из строки (например: "3.89")
     */
    static fromString(amountStr: string): Money {
        const amount = parseFloat(amountStr);
        if (isNaN(amount)) {
            throw new Error('Invalid amount string');
        }
        return Money.fromAmount(amount);
    }

    /**
     * Возвращает сумму в копейках
     */
    getCents(): number {
        return this.cents;
    }

    /**
     * Возвращает сумму в обычном формате (например: 3.89)
     */
    getAmount(): number {
        return this.cents / 100;
    }

    /**
     * Прибавляет переданную сумму
     * @param other - экземпляр Money для сложения
     */
    add(other: Money): Money {
        return new Money(this.cents + other.cents);
    }

    /**
     * Вычитает переданную сумму
     * @param other - экземпляр Money для вычитания
     */
    subtract(other: Money): Money {
        const result = this.cents - other.cents;
        if (result < 0) {
            throw new Error('Result cannot be negative');
        }
        return new Money(result);
    }

    /**
     * Умножает сумму на коэффициент
     * @param multiplier - коэффициент умножения
     */
    multiply(multiplier: number): Money {
        const result = Math.round(this.cents * multiplier);
        if (result < 0) {
            throw new Error('Result cannot be negative');
        }
        return new Money(result);
    }

    /**
     * Делит сумму на коэффициент
     * @param divisor - коэффициент деления
     */
    divide(divisor: number): Money {
        if (divisor === 0) {
            throw new Error('Division by zero');
        }
        const result = Math.round(this.cents / divisor);
        if (result < 0) {
            throw new Error('Result cannot be negative');
        }
        return new Money(result);
    }

    /**
     * Сравнивает с другой суммой
     * @param other - экземпляр Money для сравнения
     */
    equals(other: Money): boolean {
        return this.cents === other.cents;
    }

    /**
     * Проверяет, больше ли текущая сумма
     * @param other - экземпляр Money для сравнения
     */
    greaterThan(other: Money): boolean {
        return this.cents > other.cents;
    }

    /**
     * Проверяет, меньше ли текущая сумма
     * @param other - экземпляр Money для сравнения
     */
    lessThan(other: Money): boolean {
        return this.cents < other.cents;
    }

    /**
     * Процент от суммы
     * @param percentage - процент (например: 10 для 10%)
     */
    percentage(percentage: number): Money {
        return this.multiply(percentage / 100);
    }

    /**
     * Проверка на ноль
     */
    isZero(): boolean {
        return this.cents === 0;
    }

    /**
     * Форматирует сумму в строку
     */
    format(): string {
        return this.getAmount().toFixed(2);
    }

    /**
     * Возвращает строковое представление
     */
    toString(): string {
        return `${this.format()} (${this.cents} коп.)`;
    }
}